import React, { Suspense, useEffect, useState } from 'react';
import { Box, LinearProgress } from '@mui/material';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { studySections, studySectionEntries, StudySectionKey } from './studySections';
import { researcherContentSx, researcherPageSx } from '../researcherPresentation';

const panels = studySectionEntries.filter(([, section]) => section.Component);

// Keep existing bookmarks working inside the study instead of opening another page.
export function StudySectionRedirect({ section }: { section: StudySectionKey }) {
  const params = useParams();
  const [search] = useSearchParams();
  const studyId = params.studyId || search.get('study') || search.get('studyId');
  if (!studyId) return <Navigate to="/researcher/dashboard" replace />;
  const query = new URLSearchParams(search);
  query.delete('study');
  query.delete('studyId');
  query.set('tab', String(studySections[section].tab));
  return <Navigate to={`/researcher/study/${encodeURIComponent(studyId)}?${query}`} replace />;
}

export default function StudySectionPanels({ studyId, tab }: {
  studyId: string; tab: number;
}) {
  const active = panels.find(([, view]) => view.tab === tab)?.[1];
  const [visited, setVisited] = useState<number[]>([]);
  useEffect(() => {
    if (active) setVisited(previous => previous.includes(active.tab) ? previous : [...previous, active.tab]);
  }, [active]);

  return <Box hidden={!active} sx={researcherPageSx}>
    <Box sx={researcherContentSx}>
      {panels.map(([key, view]) => <Box key={key} role="tabpanel" id={`study-section-panel-${key}`}
        aria-label={view.label} hidden={view.tab !== tab}>
        {/* Mount on the first visit, then retain each view's selections and results. */}
        {(view.tab === tab || visited.includes(view.tab)) && view.Component && <Suspense fallback={<LinearProgress aria-label={`Loading ${view.label}`} />}>
          <view.Component studyId={studyId} active={view.tab === tab} />
        </Suspense>}
      </Box>)}
    </Box>
  </Box>;
}
